import {
  AlertTriangle,
  Check,
  Copy,
  Eye,
  EyeOff,
  Link2,
  Pencil,
  RefreshCcw,
  Send,
  Trash2,
} from "lucide-react";
import { useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaAlertDialog } from "@/shared/components/feedback/ColocaAlertDialog";
import { ColocaSwitch } from "@/shared/components/forms/ColocaSwitch";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatDateTime, formatTimeAgo } from "@/shared/utils/dateTimeHelpers";

import type { OrganizationWebhookConfig } from "../../services/organizationWebhooksService";
import { endpointState } from "../../utils/organizationWebhooks.utils";

export interface WebhookConfigCardProps {
  config: OrganizationWebhookConfig;
  canWrite: boolean;
  canDelete: boolean;
  isSubmitting: boolean;
  /** The live secret once revealed; `null` keeps the masked value on screen. */
  fullSecret: string | null;
  isRevealingSecret: boolean;
  onRevealSecret: () => void;
  onHideSecret: () => void;
  onEditUrl: () => void;
  onToggleActive: (isActive: boolean) => void;
  onSendTest: () => void;
  onRotateSecret: () => Promise<boolean>;
  onDelete: () => Promise<boolean>;
}

/**
 * The endpoint as a receiver console: where events go, what proves they came from Coloca, and whether the
 * receiver has been answering. The health strip reads like a probe line — three mono-labelled cells — because
 * "failing since" is what an integrator opens this screen to learn.
 */
export function WebhookConfigCard({
  config,
  canWrite,
  canDelete,
  isSubmitting,
  fullSecret,
  isRevealingSecret,
  onRevealSecret,
  onHideSecret,
  onEditUrl,
  onToggleActive,
  onSendTest,
  onRotateSecret,
  onDelete,
}: WebhookConfigCardProps) {
  const { t } = useTranslations("settings");
  const [rotateOpen, setRotateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);

  const state = endpointState(config);
  const isFailing = config.consecutive_failures > 0;

  const handleCopySecret = () => {
    if (!fullSecret) return;
    navigator.clipboard
      .writeText(fullSecret)
      .then(() => {
        setSecretCopied(true);
        setTimeout(() => setSecretCopied(false), 2000);
      })
      .catch(() => undefined);
  };

  return (
    <div className="overflow-hidden rounded-md border border-border">
      {/* Endpoint + switch */}
      <div className="flex items-start gap-3 px-4 py-3.5">
        <div className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-md border border-border bg-muted/40 text-muted-foreground">
          <Link2 size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-1 font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
            {t("webhooks_endpoint_label")}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <code
              className="min-w-0 max-w-full truncate font-mono text-[13px] text-foreground"
              title={config.url}
            >
              {config.url}
            </code>
            {state === "active" && (
              <ColocaStatusPill variant="success" size="sm" withDot>
                {t("webhooks_status_active")}
              </ColocaStatusPill>
            )}
            {state === "paused" && (
              <ColocaStatusPill variant="neutral" size="sm" withDot>
                {t("webhooks_status_paused")}
              </ColocaStatusPill>
            )}
            {state === "auto_disabled" && (
              <ColocaStatusPill variant="danger" size="sm" icon={AlertTriangle}>
                {t("webhooks_status_auto_disabled")}
              </ColocaStatusPill>
            )}
          </div>
        </div>
        {/* The switch stays operable even while the endpoint is paused for a failed
            handshake. Activation is gated by the backend, which re-runs the
            handshake LIVE and refuses the change if the receiver does not echo the
            secret — so disabling the switch on the stored `disabled_handshake_failed`
            would lock the customer out of the one gesture that clears it once they
            have fixed their server. The bands below carry the contract instead. */}
        <ColocaSwitch
          checked={config.is_active}
          onCheckedChange={onToggleActive}
          disabled={!canWrite || isSubmitting}
          aria-label={t("webhooks_toggle_aria")}
        />
      </div>

      {state === "auto_disabled" && (
        <div className="border-t border-border px-4 py-3">
          <ColocaAlert
            variant="destructive"
            icon={AlertTriangle}
            text={
              config.disabled_reason === "disabled_handshake_failed"
                ? t("webhooks_disabled_reason_handshake")
                : config.disabled_reason === "auto_disabled_prolonged_failure"
                  ? t("webhooks_disabled_reason_prolonged")
                  : t("webhooks_disabled_reason_streak")
            }
          />
        </div>
      )}
      {state === "paused" && (
        <div className="space-y-1 border-t border-border px-4 py-2.5 text-[12px] text-muted-foreground">
          <p>{t("webhooks_paused_hint")}</p>
          {/* What the switch will actually do — activation re-runs the handshake, so
              a receiver that is not answering with the secret cannot be switched on. */}
          <p>{t("webhooks_activate_blocked")}</p>
        </div>
      )}

      {/* Secret */}
      <div className="border-t border-border bg-muted/20 px-4 py-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <div className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
            {t("webhooks_secret_label")}
          </div>
          <code className="min-w-0 max-w-full break-all font-mono text-[12.5px] text-foreground">
            {fullSecret ?? config.secret_masked}
          </code>
          {fullSecret ? (
            <>
              <ColocaButton
                variant="default"
                size="xs"
                icon={secretCopied ? Check : Copy}
                onClick={handleCopySecret}
              >
                {secretCopied ? t("webhooks_secret_copied") : t("webhooks_secret_copy")}
              </ColocaButton>
              <ColocaButton variant="ghost" size="xs" icon={EyeOff} onClick={onHideSecret}>
                {t("webhooks_secret_hide")}
              </ColocaButton>
            </>
          ) : (
            <ColocaButton
              variant="ghost"
              size="xs"
              icon={Eye}
              loading={isRevealingSecret}
              disabled={!canWrite}
              onClick={onRevealSecret}
            >
              {t("webhooks_secret_reveal")}
            </ColocaButton>
          )}
        </div>
        <p className="mt-1.5 text-[11.5px] text-muted-foreground">
          {t("webhooks_secret_header_hint", { header: config.secret_header })}
        </p>
      </div>

      {/* Health strip */}
      <dl className="grid grid-cols-1 divide-y divide-border border-t border-border sm:grid-cols-3 sm:divide-x sm:divide-y-0">
        <HealthCell
          label={t("webhooks_health_failures")}
          value={String(config.consecutive_failures)}
          tone={isFailing ? "danger" : "default"}
        />
        <HealthCell
          label={t("webhooks_health_last_success")}
          value={config.last_success_at ? formatTimeAgo(config.last_success_at) : t("webhooks_health_never")}
          title={config.last_success_at ? formatDateTime(config.last_success_at) : undefined}
          tone={config.last_success_at ? "default" : "muted"}
        />
        <HealthCell
          label={t("webhooks_health_last_failure")}
          value={config.last_failure_at ? formatTimeAgo(config.last_failure_at) : t("webhooks_health_never")}
          title={config.last_failure_at ? formatDateTime(config.last_failure_at) : undefined}
          tone={config.last_failure_at && isFailing ? "danger" : "muted"}
        />
      </dl>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2 border-t border-border px-4 py-3">
        <ColocaButton
          variant="primary"
          size="sm"
          icon={Send}
          onClick={onSendTest}
          disabled={!canWrite}
          loading={isSubmitting}
        >
          {t("webhooks_action_test")}
        </ColocaButton>
        <ColocaButton
          variant="default"
          size="sm"
          icon={Pencil}
          onClick={onEditUrl}
          disabled={!canWrite || isSubmitting}
        >
          {t("webhooks_action_edit_url")}
        </ColocaButton>
        <ColocaButton
          variant="default"
          size="sm"
          icon={RefreshCcw}
          onClick={() => setRotateOpen(true)}
          disabled={!canWrite || isSubmitting}
        >
          {t("webhooks_action_rotate")}
        </ColocaButton>
        <div className="flex-1" />
        <ColocaButton
          variant="destructive"
          size="sm"
          icon={Trash2}
          onClick={() => setDeleteOpen(true)}
          disabled={!canDelete || isSubmitting}
        >
          {t("webhooks_action_delete")}
        </ColocaButton>
      </div>

      <ColocaAlertDialog
        open={rotateOpen}
        onOpenChange={setRotateOpen}
        media={<RefreshCcw className="size-8" />}
        title={t("webhooks_rotate_title")}
        description={t("webhooks_rotate_desc")}
        confirmLabel={t("webhooks_rotate_confirm")}
        cancelLabel={t("webhooks_url_cancel")}
        confirmDisabled={isSubmitting}
        onConfirm={() => {
          void onRotateSecret().then((done) => {
            if (done) setRotateOpen(false);
          });
        }}
      />

      <ColocaAlertDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        media={<Trash2 className="size-8" />}
        title={t("webhooks_delete_title")}
        description={t("webhooks_delete_desc")}
        confirmLabel={t("webhooks_delete_confirm")}
        confirmVariant="destructive"
        cancelLabel={t("webhooks_url_cancel")}
        confirmDisabled={isSubmitting}
        onConfirm={() => {
          void onDelete().then((done) => {
            if (done) setDeleteOpen(false);
          });
        }}
      />
    </div>
  );
}

function HealthCell({
  label,
  value,
  title,
  tone,
}: {
  label: string;
  value: string;
  title?: string;
  tone: "default" | "danger" | "muted";
}) {
  return (
    <div className="px-4 py-2.5">
      <dt className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">{label}</dt>
      <dd
        title={title}
        className={cn(
          "mt-0.5 font-mono text-[13px] tabular-nums",
          tone === "danger" && "text-destructive",
          tone === "muted" && "text-muted-foreground",
          tone === "default" && "text-foreground",
        )}
      >
        {value}
      </dd>
    </div>
  );
}
